import { useCallback, useEffect, useMemo, useState } from 'react'

import { MessagePreview } from '@liveHelper/context/MessageChannelContext/MessagePreview'
import { messagesSelector, useStore } from '@liveHelper/models/store'
import type { MessageAttachment } from '@oneToOne/shared/components/chat/types'
import { transformMessage } from '@oneToOne/shared/utils/transformMessage'

import { useLocalStorage } from '@core/hooks/useLocalStorage'
import { usePrevious } from '@core/hooks/usePrevious'

export interface LastUnreadMessage {
  avatar: string
  name: string
  message: string
  attachment: MessageAttachment | undefined
  numberOfUnreadMessages: number
}

interface ReturnProps {
  lastUnreadMessage: LastUnreadMessage | undefined
  markLatestMessageAsRead: () => void
  dismissMessagePreview: () => void
  showMessagePreview: () => void
}

/**
 * Returns the last unread message and a function to update it.
 */
export const useMessagePreview = (): ReturnProps => {
  const messages = useStore(messagesSelector)
  const prevMessages = usePrevious(messages)
  const [messagePreview, setMessagePreview] = useLocalStorage('messagePreview')
  const [lastUnreadMessage, setLastUnreadMessage] = useState<
    LastUnreadMessage | undefined
  >(undefined)

  const messagePreviewObj = useMemo(
    () => MessagePreview.fromJSON(messagePreview),
    [messagePreview],
  )

  const lastAgentMessages = useMemo(
    () =>
      messages.filter(
        // m.id is needed here because there are fake author messages created from the frontend
        (m) =>
          (m.text_message?.author === 'AUTHOR_AGENT' ||
            m.media_message?.author === 'AUTHOR_AGENT') &&
          m.id,
      ),
    [messages],
  )

  useEffect(() => {
    if (messagePreviewObj?.getDismissed()) {
      setLastUnreadMessage(undefined)
    }

    if (prevMessages?.length !== messages.length) {
      if (lastAgentMessages.length > 0) {
        const lastAgentMessage = transformMessage(
          lastAgentMessages[lastAgentMessages.length - 1],
          'visitor',
        )

        if (
          lastAgentMessage.id === messagePreviewObj?.getLastReadMessageId() &&
          messagePreviewObj?.getDismissed()
        ) {
          // If the last read message id is the same as the last agent message id and it has been dismissed previously,
          // then we can assume that the user has read the message and we can dismiss the message preview.
          setLastUnreadMessage(undefined)
        } else {
          const avatar =
            lastAgentMessage.text_message?.profile_image_url ||
            lastAgentMessage.media_message?.profile_image_url ||
            ''
          const name =
            lastAgentMessage.text_message?.name ||
            lastAgentMessage.media_message?.name ||
            ''

          if (messagePreviewObj) {
            const lastReadMessagePos = lastAgentMessages.findIndex(
              (m) => m.id === messagePreviewObj.getLastReadMessageId(),
            )
            const unreadNumber =
              lastAgentMessages.length - 1 - lastReadMessagePos

            if (unreadNumber > 0) {
              setLastUnreadMessage({
                avatar,
                name,
                message: lastAgentMessage.content,
                attachment: lastAgentMessage.attachment,
                numberOfUnreadMessages: unreadNumber,
              })
            } else {
              setLastUnreadMessage(undefined)
            }
          } else {
            setLastUnreadMessage({
              avatar,
              name,
              message: lastAgentMessage.content,
              attachment: lastAgentMessage.attachment,
              numberOfUnreadMessages: lastUnreadMessage?.numberOfUnreadMessages
                ? lastUnreadMessage?.numberOfUnreadMessages + 1
                : lastAgentMessages.length,
            })
          }
        }
      }
    }
  }, [
    lastAgentMessages,
    lastUnreadMessage?.numberOfUnreadMessages,
    messagePreviewObj,
    messages,
    prevMessages,
    prevMessages?.length,
    setMessagePreview,
  ])

  const markLatestMessageAsRead = useCallback(() => {
    if (lastAgentMessages.length > 0) {
      const lastAgentMessage = transformMessage(
        lastAgentMessages[lastAgentMessages.length - 1],
        'visitor',
      )

      if (lastAgentMessage.id) {
        if (messagePreviewObj) {
          messagePreviewObj.updateLastReadMessageId(lastAgentMessage.id)
          setMessagePreview(messagePreviewObj.toJSON())
        } else {
          setMessagePreview(
            JSON.stringify({
              lastReadMessageId: lastAgentMessage.id,
              dismissed: false,
            }),
          )
        }
      }
    }
    setLastUnreadMessage(undefined)
  }, [lastAgentMessages, messagePreviewObj, setMessagePreview])

  const dismissMessagePreview = useCallback(() => {
    if (messagePreviewObj) {
      messagePreviewObj.dismiss()
      setMessagePreview(messagePreviewObj.toJSON())
    }
    setLastUnreadMessage(undefined)
  }, [messagePreviewObj, setMessagePreview])

  const showMessagePreview = useCallback(() => {
    if (messagePreviewObj) {
      messagePreviewObj.show()
      setMessagePreview(messagePreviewObj.toJSON())
    }
  }, [messagePreviewObj, setMessagePreview])

  return {
    lastUnreadMessage,
    markLatestMessageAsRead,
    dismissMessagePreview,
    showMessagePreview,
  }
}
